import React, { useState, useEffect } from 'react';
import { apiGet } from '../../utils/apiAuth';

const FileBrowser = ({ isOpen, onClose, onImageSelect, selectedSubTopic }) => {
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFolders, setFilteredFolders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Load folders when popup opens
  useEffect(() => {
    if (isOpen) {
      loadFolders();
    }
  }, [isOpen]);

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      // Focus search input with Ctrl+F or Cmd+F
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search folders"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
      
      // Clear search with Escape
      if (e.key === 'Escape' && searchQuery) {
        clearSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchQuery]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/content_creation/image-file-browser/');
      
      if (response && response.data && response.data.success && response.data.data) {
        setFolders(response.data.data.folders || []);
        setImages([]);
        setCurrentFolder(null);
      } else {
        setError('Failed to load folders - Invalid response structure');
      }
    } catch (err) {
      setError('Error loading folders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadImages = async (subtopicName) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet(`/api/content_creation/image-file-browser/?subtopic_name=${encodeURIComponent(subtopicName)}`);
      
      if (response && response.data && response.data.success && response.data.data) {
        setImages(response.data.data.files || []);
        setCurrentFolder(subtopicName);
      } else {
        setError('Failed to load images - Invalid response structure');
      }
    } catch (err) {
      setError('Error loading images: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    if (isMultiSelectMode) {
      // Toggle selection in multi-select mode
      setSelectedImages(prev => {
        const isSelected = prev.some(selected => selected.url === image.url);
        if (isSelected) {
          return prev.filter(selected => selected.url !== image.url);
        } else {
          return [...prev, image];
        }
      });
    } else {
      // Single select mode - insert immediately
      onImageSelect(image.url);
      setTimeout(() => {
        onClose();
      }, 500);
    }
  };

  const handleMultiSelectToggle = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      setSelectedImages([]);
    }
  };

  const handleInsertSelected = () => {
    if (selectedImages.length === 0) return;
    
    // Insert all selected images
    selectedImages.forEach((image, index) => {
      setTimeout(() => {
        onImageSelect(image.url);
      }, index * 100); // Stagger the insertions
    });
    
    // Close after all images are inserted
    setTimeout(() => {
      onClose();
    }, selectedImages.length * 100 + 500);
  };

  const handleSelectAll = () => {
    setSelectedImages(images);
  };

  const handleClearSelection = () => {
    setSelectedImages([]);
  };

  const handleBackToFolders = () => {
    setCurrentFolder(null);
    setImages([]);
    setSelectedImages([]);
    setIsMultiSelectMode(false);
  };

  // World-class search mechanism
  const performSearch = (query, folderList) => {
    if (!query.trim()) {
      return folderList;
    }

    const normalizedQuery = query.toLowerCase().trim();
    
    return folderList.filter(folder => {
      const folderName = folder.name || '';
      const formattedFolderName = formatFolderName(folderName);
      const normalizedFolderName = formattedFolderName.toLowerCase();
      
      // Multiple search strategies for best results
      return (
        // Exact match
        normalizedFolderName === normalizedQuery ||
        // Starts with query
        normalizedFolderName.startsWith(normalizedQuery) ||
        // Contains query
        normalizedFolderName.includes(normalizedQuery) ||
        // Word boundary match (matches whole words)
        new RegExp(`\\b${normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(normalizedFolderName) ||
        // Fuzzy match (allows for character transpositions and missing characters)
        fuzzyMatch(normalizedFolderName, normalizedQuery) ||
        // Partial word match (matches parts of words)
        normalizedFolderName.split(/\s+/).some(word => 
          word.startsWith(normalizedQuery) || 
          normalizedQuery.split(/\s+/).some(queryWord => 
            word.includes(queryWord) || queryWord.includes(word)
          )
        )
      );
    });
  };

  // Advanced fuzzy matching algorithm
  const fuzzyMatch = (text, query) => {
    if (query.length === 0) return true;
    if (query.length > text.length) return false;
    
    let queryIndex = 0;
    let textIndex = 0;
    
    while (textIndex < text.length && queryIndex < query.length) {
      if (text[textIndex] === query[queryIndex]) {
        queryIndex++;
      }
      textIndex++;
    }
    
    return queryIndex === query.length;
  };

  // Handle search input with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      setIsSearching(true);
      setShowSearchResults(true);
      
      // Debounce search for better performance
      const timeoutId = setTimeout(() => {
        const results = performSearch(query, folders);
        setSearchResults(results);
        setIsSearching(false);
      }, 150); // 150ms debounce
      
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsSearching(false);
  };

  // Get folders to display (search results or all folders)
  const getDisplayFolders = () => {
    if (showSearchResults && searchQuery.trim()) {
      return searchResults;
    }
    return folders;
  };

  // Format folder name for display (replace underscores with spaces)
  const formatFolderName = (name) => {
    return name.replace(/_/g, ' ');
  };

  // Highlight search terms in folder names
  const highlightSearchTerm = (text, query) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark style="background-color: #fff3cd; color: #856404; padding: 2px 4px; border-radius: 4px; font-weight: 600;">$1</mark>');
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes slideIn {
            from { 
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .file-browser-overlay {
            animation: fadeIn 0.3s ease-out;
          }
          
          .file-browser-popup {
            animation: slideIn 0.3s ease-out;
          }
        `}
      </style>
    <div className="file-browser-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div className="file-browser-popup" style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '0',
        maxWidth: '95%',
        maxHeight: '95%',
        width: '800px',
        height: '600px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'slideIn 0.3s ease-out'
      }}>
        {/* Header */}
        <div className="file-browser-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          minHeight: '36px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px'
            }}>
              {currentFolder ? '🖼️' : '📁'}
            </div>
            <h4 style={{ 
              margin: 0, 
              color: 'white', 
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '-0.2px'
            }}>
              {currentFolder ? `Images in ${currentFolder}` : 'Browse Images'}
            </h4>
          </div>
          
          <button
            onClick={onClose}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              fontWeight: '300'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            ×
          </button>
        </div>

         {/* Back button and Multi-select toggle when viewing images */}
         {currentFolder && (
           <div style={{ 
             padding: '6px 12px',
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             backgroundColor: '#f8f9fa',
             borderBottom: '1px solid #e9ecef'
           }}>
             <button
               onClick={handleBackToFolders}
               style={{
                 padding: '4px 8px',
                 backgroundColor: '#ffffff',
                 color: '#495057',
                 border: '1px solid #e9ecef',
                 borderRadius: '4px',
                 cursor: 'pointer',
                 fontSize: '11px',
                 fontWeight: '500',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s ease',
                 boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
               }}
               onMouseEnter={(e) => {
                 e.target.style.backgroundColor = '#f1f5f9';
                 e.target.style.borderColor = '#cbd5e1';
                 e.target.style.transform = 'translateY(-1px)';
               }}
               onMouseLeave={(e) => {
                 e.target.style.backgroundColor = '#ffffff';
                 e.target.style.borderColor = '#e9ecef';
                 e.target.style.transform = 'translateY(0)';
               }}
            >
              <span style={{ fontSize: '12px' }}>←</span>
              Back
            </button>

             {/* Multi-select toggle */}
             <button
               onClick={handleMultiSelectToggle}
               style={{
                 padding: '4px 8px',
                 backgroundColor: isMultiSelectMode ? '#667eea' : '#ffffff',
                 color: isMultiSelectMode ? 'white' : '#495057',
                 border: `1px solid ${isMultiSelectMode ? '#667eea' : '#e9ecef'}`,
                 borderRadius: '4px',
                 cursor: 'pointer',
                 fontSize: '11px',
                 fontWeight: '500',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s ease',
                 boxShadow: isMultiSelectMode ? '0 2px 4px rgba(102, 126, 234, 0.3)' : '0 1px 2px rgba(0, 0, 0, 0.05)'
               }}
               onMouseEnter={(e) => {
                 if (isMultiSelectMode) {
                   e.target.style.backgroundColor = '#5a67d8';
                   e.target.style.transform = 'translateY(-1px)';
                 } else {
                   e.target.style.backgroundColor = '#f1f5f9';
                   e.target.style.borderColor = '#cbd5e1';
                   e.target.style.transform = 'translateY(-1px)';
                 }
               }}
               onMouseLeave={(e) => {
                 if (isMultiSelectMode) {
                   e.target.style.backgroundColor = '#667eea';
                   e.target.style.transform = 'translateY(0)';
                 } else {
                   e.target.style.backgroundColor = '#ffffff';
                   e.target.style.borderColor = '#e9ecef';
                   e.target.style.transform = 'translateY(0)';
                 }
               }}
            >
              <span>{isMultiSelectMode ? '✓' : '☐'}</span>
              {isMultiSelectMode ? 'Multi' : 'Select'}
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ 
              margin: 0, 
              color: '#6c757d',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {currentFolder ? 'Loading images...' : 'Loading folders...'}
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{
            margin: '12px 20px',
            padding: '12px 16px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px' }}>⚠️</span>
            {error}
          </div>
        )}

        {/* Multi-select bulk actions */}
        {currentFolder && isMultiSelectMode && images.length > 0 && (
          <div style={{
            padding: '8px 20px',
            backgroundColor: '#f8f9ff',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={handleSelectAll}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#5a67d8';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#667eea';
                }}
              >
                All
              </button>
              
              <button
                onClick={handleClearSelection}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#cbd5e0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#e2e8f0';
                }}
              >
                Clear
              </button>
            </div>
            
            <div style={{
              fontSize: '12px',
              color: '#4a5568',
              fontWeight: '500'
            }}>
              {selectedImages.length}/{images.length}
            </div>
          </div>
        )}

        {/* Search Bar - Only show when not viewing images */}
        {!currentFolder && !loading && !error && (
          <div style={{
            padding: '0 20px 12px 20px',
            borderBottom: '1px solid #e9ecef'
          }}>
            <div style={{
              position: 'relative',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
                transition: 'all 0.3s ease',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '0 12px',
                  color: '#6c757d',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {isSearching ? (
                    <div style={{
                      width: '12px',
                      height: '12px',
                      border: '2px solid #e9ecef',
                      borderTop: '2px solid #667eea',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  ) : (
                    '🔍'
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Search folders..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={(e) => {
                    e.target.parentElement.style.borderColor = '#667eea';
                    e.target.parentElement.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.parentElement.style.borderColor = '#e9ecef';
                    e.target.parentElement.style.boxShadow = 'none';
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 0',
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    fontSize: '14px',
                    color: '#2d3748',
                    fontWeight: '500'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#6c757d',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#dc3545';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#6c757d';
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* Search Results Info */}
              {showSearchResults && searchQuery.trim() && (
                <div style={{
                  marginTop: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: '#6c757d',
                  fontWeight: '500'
                }}>
                  {isSearching ? (
                    <span>Searching...</span>
                  ) : (
                    <span>
                      {searchResults.length === 0 ? (
                        <span style={{ color: '#dc3545' }}>
                          No folders found
                        </span>
                      ) : (
                        <span style={{ color: '#28a745' }}>
                          {searchResults.length} folder{searchResults.length !== 1 ? 's' : ''} found
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

         {/* Content */}
         {!loading && !error && (
           <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
            {!currentFolder ? (
              // Folders view
              <div className="folders-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '8px'
              }}>
                {getDisplayFolders().map((folder, index) => (
                  <div
                    key={index}
                    onClick={() => loadImages(folder.name)}
                    style={{
                      padding: '8px',
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.backgroundColor = '#f8f9ff';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e9ecef';
                      e.target.style.backgroundColor = '#ffffff';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      margin: '0 auto 12px auto',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                    }}>
                      📁
                    </div>
                    <div 
                      style={{ 
                        fontWeight: '600', 
                        marginBottom: '6px',
                        fontSize: '14px',
                        color: '#2d3748',
                        letterSpacing: '-0.2px'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightSearchTerm(formatFolderName(folder.name), searchQuery)
                      }}
                    />
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#718096',
                      fontWeight: '500',
                      padding: '4px 8px',
                      backgroundColor: '#f7fafc',
                      borderRadius: '12px',
                      display: 'inline-block'
                    }}>
                      {folder.files} image{folder.files !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
                {folders.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#a0aec0', 
                    padding: '40px 20px',
                    gridColumn: '1 / -1',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>📂</div>
                    No folders found
                  </div>
                )}
              </div>
            ) : (
              // Images view
              <div className="images-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '6px'
              }}>
                {images.map((image, index) => {
                  const isSelected = selectedImages.some(selected => selected.url === image.url);
                  return (
                    <div
                      key={index}
                      onClick={() => handleImageClick(image)}
                      style={{
                        border: `1px solid ${isSelected ? '#10b981' : '#e9ecef'}`,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: isSelected ? '#f0fdf4' : '#ffffff',
                        boxShadow: isSelected ? '0 2px 8px rgba(16, 185, 129, 0.2)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        group: 'image-card',
                        transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                      }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.transform = 'translateY(-1px) scale(1.02)';
                        e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                      }
                    }}
                  >
                    {/* Selection indicator */}
                    {isMultiSelectMode && (
                      <div style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: 'white',
                        zIndex: 10,
                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)'
                      }}>
                        {isSelected ? '✓' : '☐'}
                      </div>
                    )}
                    
                    <div style={{ position: 'relative', height: '100px', overflow: 'hidden' }}>
                      <img
                        src={image.url}
                        alt={image.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{
                        display: 'none',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa',
                        fontSize: '24px'
                      }}>
                        🖼️
                      </div>
                      {/* Hover overlay */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isMultiSelectMode 
                          ? (isSelected 
                              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)'
                              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)')
                          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '0';
                      }}
                      >
                        {isMultiSelectMode 
                          ? (isSelected ? 'Click to Deselect' : 'Click to Select')
                          : 'Click to Insert'
                        }
                      </div>
                    </div>
                    <div style={{
                      padding: '8px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '2px',
                        wordBreak: 'break-word',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {image.name}
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: '#718096',
                        fontWeight: '500'
                      }}>
                        {(image.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                  );
                })}
                {images.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#a0aec0', 
                    padding: '40px 20px',
                    gridColumn: '1 / -1',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>🖼️</div>
                    No images found in this folder
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Insert button at bottom - Only show when in multi-select mode with selected images */}
        {currentFolder && isMultiSelectMode && selectedImages.length > 0 && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f8f9ff',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <button
              onClick={handleInsertSelected}
              style={{
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                minWidth: '160px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#059669';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#10b981';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
              }}
            >
              <span style={{ fontSize: '14px' }}>📎</span>
              Insert {selectedImages.length} Image{selectedImages.length !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default FileBrowser;
